import React, { useEffect, useMemo, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { useTranslation } from 'react-i18next'
import { Home, Loader2, FolderInput } from 'lucide-react'
import FolderTree, { folderRowClassName, type FolderTreeNode } from '@/components/doc-control/FolderTree'
import { folderIndentClass } from '@/components/doc-control/folder-tree'
import { cn } from '@/lib/utils'

type FolderNode = FolderTreeNode

interface MoveToFolderModalProps {
  isOpen: boolean
  folders: FolderNode[]
  movingFolderIds: string[]
  hasDocuments: boolean
  // The items' current location: a folder id, null for the project root, or
  // undefined to disable no destination. Selecting it would be a no-op move.
  currentLocationId?: string | null
  loading: boolean
  onConfirm: (targetFolderId: string | null) => void
  onCancel: () => void
}

// A folder being moved, plus all of its descendants, are invalid destinations.
function collectSubtreeIds(folders: FolderNode[], rootIds: string[]): Set<string> {
  const byId = new Map<string, FolderNode>()
  const index = (list: FolderNode[]) => {
    for (const f of list) {
      byId.set(f.id, f)
      if (f.children) index(f.children)
    }
  }
  index(folders)

  const disabled = new Set<string>()
  const addSubtree = (id: string) => {
    if (disabled.has(id)) return
    disabled.add(id)
    const f = byId.get(id)
    if (f?.children) for (const c of f.children) addSubtree(c.id)
  }
  for (const id of rootIds) addSubtree(id)
  return disabled
}

const MoveToFolderModal: React.FC<MoveToFolderModalProps> = ({
  isOpen,
  folders,
  movingFolderIds,
  hasDocuments,
  currentLocationId,
  loading,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation()
  // undefined = nothing chosen yet; null = root; string = a folder id
  const [selectedTargetId, setSelectedTargetId] = useState<string | null | undefined>(undefined)
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set())

  const disabledIds = useMemo(
    () => collectSubtreeIds(folders, movingFolderIds),
    [folders, movingFolderIds]
  )

  // Selecting the items' current location is a pointless no-op; disable it.
  // currentLocationId === null means "currently at root", so Root is the no-op.
  const rootDisabled = hasDocuments || currentLocationId === null

  useEffect(() => {
    if (isOpen) {
      setSelectedTargetId(undefined)
      setExpanded(new Set())
    }
  }, [isOpen])

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open && !loading) onCancel() }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderInput className="h-4 w-4" />
            {t('DocControl_move_dialog_title')}
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">
          {t('DocControl_move_select_destination')}
        </p>

        <div className="max-h-[320px] space-y-0.5 overflow-y-auto rounded-md border p-1.5">
          <div
            role="button"
            tabIndex={rootDisabled ? -1 : 0}
            aria-pressed={selectedTargetId === null}
            aria-disabled={rootDisabled || undefined}
            className={cn(
              folderRowClassName({ selected: selectedTargetId === null && !rootDisabled, disabled: rootDisabled }),
              folderIndentClass(0),
            )}
            onClick={() => !rootDisabled && setSelectedTargetId(null)}
            onKeyDown={(e) => {
              if ((e.key === 'Enter' || e.key === ' ') && !rootDisabled) {
                e.preventDefault()
                setSelectedTargetId(null)
              }
            }}
          >
            <span className="h-5 w-5 shrink-0" />
            <Home className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="truncate">{t('DocControl_move_root_option')}</span>
          </div>

          <FolderTree
            folders={folders}
            selectedId={selectedTargetId ?? null}
            onSelect={setSelectedTargetId}
            expanded={expanded}
            onExpandedChange={setExpanded}
            isDisabled={(folder) => disabledIds.has(folder.id) || folder.id === currentLocationId}
            aria-label={t('DocControl_move_select_destination')}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={loading}>
            {t('Common_cancel')}
          </Button>
          <Button
            onClick={() => selectedTargetId !== undefined && onConfirm(selectedTargetId)}
            disabled={loading || selectedTargetId === undefined}
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FolderInput className="h-4 w-4" />}
            {t('DocControl_move')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default MoveToFolderModal
